import React, { useState, useEffect, useRef, useCallback } from 'react';
import { favoritesListSelector } from 'store/selectors/userFavoritesSelector';
import { getFeatureFlagByNameSelector } from 'store/selectors/commonSelector';
import {
  setTemplate,
  setClearance,
  setPreview,
  setSkuCategory,
  resetQuickViewState,
  setEcodeParam,
  setReferrerParam,
  setOptionalParams,
  setProductQuickViewDetails,
  setDefaultColor,
  setDefaultSkuPartNumber,
} from 'store/slices/quickView';
import { useAppDispatch, useAppSelector } from 'store/store';
import { storeDetailsSelector } from 'store/selectors/locationServicesSelector';
import { siteErrorEvent } from 'utility/analytics-events';
import {
  ecodeParamSelector,
  promotionDataSelector,
  templateSelector,
} from 'store/selectors/quickViewSelector';
import { Drawer, useDelaySwitch } from '@dsg-tech/homefield-react';
import Overlay from './components/Overlay/Overlay';
import useQuickviewProductDetails from './hooks/useQuickviewProductDetails';
import useLogger from '../../hooks/useLogger';
import QuickviewAnalyticsData from './analytics/QuickviewAnalyticsData';
import { quickviewProductViewEvent } from './analytics/analytics-events';
import usePromotionData from './hooks/usePromotionData';
import './Quickview.scss';
import ProductTitle from './components/ProductTitle';
import CloseButton from './components/ActionButtons/CloseButton';
import SkeletonContent from './components/Skeleton/SkeletonContent';
import ErrorContent from './components/Content/ErrorContent';
import QuickViewContent from './components/Content/QuickViewContent';
import './components/Content/QuickViewModal.scss';
import './components/Content/Content.scss';

function QuickView() {
  const storeDetails = useAppSelector(storeDetailsSelector);
  // the open/closed state of the modal
  const [open, setOpen] = useState(false);
  // Get productDetails, loading state, and error state from Quickview Product Details hook
  // setEcode is returned to update the state of the ecode in the hook from Quickview
  const [quickviewDetails, loading, error, ecode, setEcode] = useQuickviewProductDetails({
    selectedStoreId: storeDetails?.location ?? storeDetails?.store,
  });
  // Quantity
  const [count, setCount] = useState(1);
  // Color
  const [color, setColor] = useState(null);
  // Resolved Single SKU
  const [sku, setSKU] = useState(null);
  // Validate Options
  const [validate, setValidate] = useState(false);
  // Validate Delivery and Pickup
  const [selectedMethod, setSelectedMethod] = useState('SHIP');
  // analytics page source
  const [referrer, setReferrer] = useState(null);
  // previously focused element before QV was opened
  const [previousFocus, setPreviousFocus] = useState();
  // skuDefaultValue recived from the openProductQuickView function
  const [skuDefaultValue, setSkuDefaultValue] = useState('');
  // additionalData recived from the openProductQuickView function
  const [additionalAnalytics, setAdditionalAnalytics] = useState(null);
  const [currentSkuInfo, setCurrentSkuInfo] = useState({});
  const favoritesList = useAppSelector(favoritesListSelector);
  const { warn } = useLogger();
  const promotionData = useAppSelector(promotionDataSelector);

  const dispatch = useAppDispatch();

  const defaultTemplate = useAppSelector(templateSelector);
  const ecodeSelector = useAppSelector(ecodeParamSelector);

  const [fetchPromotionData] = usePromotionData(quickviewDetails?.style?.catentryId ?? null);
  const favoriteIconEnabled = useAppSelector(getFeatureFlagByNameSelector('addFavoritesEnabled'));
  const quickViewDrawerEnabled = useAppSelector(
    getFeatureFlagByNameSelector('quickViewDrawerEnabled'),
  );

  const { state, neutral, switchState } = useDelaySwitch(false, 250);

  // Optional callback method
  const callbackFunction = useRef();

  const openProductQuickView = (...args) => {
    const [ecodeParam, referrerParam, optionalParams = () => null] = args;

    dispatch(setEcodeParam(ecodeParam));
    dispatch(setReferrerParam(referrerParam));
    dispatch(setOptionalParams(optionalParams));

    // Sets ignore flag to true if a plp or srlp page
    const ignoreFlag =
      window.location.href.indexOf('/f/') > -1 ||
      window.location.href.indexOf('/search/SearchDisplay') > -1;

    // If ignoreFlag is false and quickViewDisabled is set to true, take the user directly to the pdp page
    if (ignoreFlag || !window.quickviewDisabled) {
      const {
        callback = () => null,
        clearance = false,
        skuCategory = null,
        preview = false,
        skuPartNumber = '',
        defaultColor: defaultColorParam = '', // used to distinguish what was passed in vs what is stored in redux
        additionalData = {},
        template = 'cart',
      } = optionalParams;

      setValidate(false);
      setEcode(ecodeParam);
      setReferrer(referrerParam);
      dispatch(setClearance(clearance));
      dispatch(setSkuCategory(skuCategory));
      dispatch(setPreview(preview));

      dispatch(setDefaultColor(defaultColorParam));
      dispatch(setDefaultSkuPartNumber(skuPartNumber));

      setColor(defaultColorParam);
      setOpen(true);
      switchState();
      callbackFunction.current = typeof optionalParams !== 'object' ? optionalParams : callback;
      setSkuDefaultValue(skuPartNumber);
      setAdditionalAnalytics(additionalData);

      dispatch(setTemplate(template));
    } else {
      window.location.href = `/p/${ecodeParam}/${ecodeParam}`;
    }
  };

  useEffect(() => {
    window.quickview = {
      openProductQuickView,
    };
  }, []);

  useEffect(() => {
    if (ecodeSelector !== ecode) {
      dispatch(setProductQuickViewDetails([]));

      dispatch(setDefaultColor(''));
      dispatch(setDefaultSkuPartNumber(''));
    }
  }, [ecodeSelector, ecode]);

  // modal popup scroll
  useEffect(() => {
    if (open) {
      setCount(1);
      setPreviousFocus(document.activeElement);
      document.body.style.overflow = 'hidden';
    } else {
      setSKU(null);
      setColor(null);
      document.body.style.overflow = 'scroll';
      if (previousFocus) {
        previousFocus.focus();
      }
    }
  }, [open]);

  useEffect(() => {
    fetchPromotionData();
  }, [quickviewDetails?.style?.catentryId]);

  useEffect(() => {
    if (
      ecode &&
      typeof ecode === 'string' &&
      quickviewDetails?.style?.partNumber?.toUpperCase() === ecode.toUpperCase() &&
      open
    ) {
      const qvTemplateObj = {
        cart: 'Standard',
        edit: 'EditItemInCart',
        favorites: 'AddToList',
        saveSelection: 'SaveSelection',
      };

      const quickViewTemplate = qvTemplateObj[defaultTemplate];

      // Call analytics event when quickview modal opens, there are quickview details, and the quickview details partNumber matches the ecode
      try {
        const qvAnalytics = QuickviewAnalyticsData(
          quickviewDetails,
          promotionData,
          referrer,
          additionalAnalytics,
          quickViewTemplate,
        );
        const data = qvAnalytics;
        quickviewProductViewEvent(data);
      } catch (e) {
        warn('Quickview Analytics Failed: ', error);
      }
    }
  }, [quickviewDetails, ecode, open]);

  const handleClose = (origin = '') => {
    switchState();
    setOpen(false);
    switch (defaultTemplate) {
      case 'cart':
        if (favoriteIconEnabled && Object.keys(currentSkuInfo).length && validate) {
          siteErrorEvent({
            event: 'Dropped from favorites after error',
          });
        }
        break;
      case 'favorites':
        if (origin === 'addtofavorites') {
          return;
        }

        if (Object.keys(currentSkuInfo).length) {
          if (validate) {
            siteErrorEvent({
              event: 'Dropped from favorites after error',
            });
            setValidate(false);
          }
          return;
        }

        if (previousFocus) {
          previousFocus.focus();
        }

        break;
      default:
    }
    if (previousFocus) {
      previousFocus.focus();
    }

    dispatch(resetQuickViewState());
  };

  const updateSignal = useCallback(() => {
    if (favoritesList) {
      const evt = new CustomEvent('updateFavoritesStoreData', { detail: 'QV' });
      window.dispatchEvent(evt);
    }
  }, [favoritesList]);

  let Content;

  const closeRef = useRef(null);

  useEffect(() => {
    if (closeRef?.current) closeRef.current.focus();
  }, [loading]);

  if (!error && quickviewDetails?.style && quickviewDetails?.skus) {
    Content = (
      <QuickViewContent
        quickviewDetails={quickviewDetails}
        color={color}
        loading={loading}
        sku={sku}
        count={count}
        setColor={setColor}
        skuDefaultValue={skuDefaultValue}
        setSKU={setSKU}
        validate={validate}
        setValidate={setValidate}
        selectedMethod={selectedMethod}
        setSelectedMethod={setSelectedMethod}
        callback={callbackFunction.current}
        onClose={handleClose}
        setCurrentSkuInfo={setCurrentSkuInfo}
        updateSignal={updateSignal}
      />
    );
  }

  if (error || quickviewDetails?.skus === undefined || Object.keys(quickviewDetails).length === 0) {
    Content = <ErrorContent />;
  }

  return (
    <div className="quickview-container" data-testid="quickViewModal_close">
      {quickViewDrawerEnabled ? (
        <Drawer
          open={state}
          className="quickview-content hmf-position-fixed"
          onCloseModal={handleClose}
          stickyCloseButton
          closing={neutral}
          title={quickviewDetails?.style?.name}>
          <>{loading ? <SkeletonContent /> : Content}</>
        </Drawer>
      ) : (
        open && (
          <>
            <Overlay onClickOff={handleClose} key="overlay" />
            <div
              className="quickview hmf-display-flex hmf-flex-column hmf-flex-shrink-none slide-in hmf-p-md-m hmf-p-s"
              id="quickviewcard"
              data-testid="quickViewModal">
              <div
                className={
                  defaultTemplate === 'saveSelection' ? 'quickview-scrollable-content' : ''
                }>
                <div
                  className="hmf-display-flex hmf-flex-row hmf-justify-content-space-between"
                  data-testid="quickViewModal_modal_close">
                  {!loading && !error && quickviewDetails?.style && quickviewDetails?.skus && (
                    <ProductTitle name={quickviewDetails?.style?.name || null} />
                  )}
                  <CloseButton onClose={handleClose} closeRef={closeRef} />
                </div>
                <div>{loading ? <SkeletonContent /> : Content}</div>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}
export default QuickView;
